import { clsx } from 'clsx';
import { Children, cloneElement } from 'react';
import type { ReactElement } from 'react';

import type { ReeferBaseProps } from '../../utils/baseProps.types';
import type { TabProps } from './tab/tab';
import { Tab } from './tab/tab';
import styles from './tabs.module.css';

type TabDirection = 'horizontal' | 'vertical';

interface TabsProps<T extends string = string> extends ReeferBaseProps {
  /** Aria label */
  ariaLabel?: string;

  /** Only allows Tabs.Tab children */
  children: ReactElement<TabProps>[];

  /** Direction of the tabs, defaults to horizontal */
  direction?: TabDirection;

  /** Stretch to full width of container (overrides tabWidth) */
  full?: boolean;

  /** Function that returns the value of the clicked button */
  onChange?(value: T): void;

  /** Set the width of each tab */
  tabWidth?: number | string;

  /** The currently selected value */
  value: T;
}

/**
 * The `Tabs` components is used to display a series of selectable tabs.
 *
 * Each individual tab within the `Tabs` component is defined using the `Tab.Tab` component.
 */

function BaseTabs<T extends string = string>({
  ariaLabel,
  children,
  className,
  'data-testid': testId,
  direction = 'horizontal',
  full,
  id,
  onChange,
  style,
  tabWidth,
  value,
}: TabsProps<T>) {
  const renderedTabs = Children.map(children, (child) => {
    /** value for buttons, to for router links */
    const childValue = child.props.to || child.props.value;

    const isSelected = value === child.props.to || value === child.props.value;

    const onClickHandler = () => {
      child.props?.onClick && child.props.onClick();

      if (childValue) {
        onChange && onChange(childValue as T);
      }
    };

    return cloneElement(child, {
      direction,
      full,
      onClick: onClickHandler,
      selected: isSelected,
      width: tabWidth,
    });
  });

  return (
    <div
      aria-label={ariaLabel}
      className={clsx(
        className,
        styles['tabs'],
        direction === 'vertical' && styles[`tabs__direction__${direction}`],
        {
          [styles['tabs__full']]: full,
        }
      )}
      data-testid={testId}
      id={id}
      style={style}
    >
      {renderedTabs}
    </div>
  );
}

export type { TabDirection, TabsProps };

export const Tabs = Object.assign(BaseTabs, {
  Tab,
});
